import * as THREE from "three";
import type { TextureLibrary } from "./textures";

export type MatLib = Record<string, THREE.MeshStandardMaterial>;

function pbr(
  tex: TextureLibrary[keyof TextureLibrary] | undefined,
  opts: THREE.MeshPhysicalMaterialParameters = {},
  repeat: [number, number] = [2, 2]
) {
  const set = tex as { map?: THREE.Texture; normalMap?: THREE.Texture; roughnessMap?: THREE.Texture } | undefined;
  const cloneMap = (t?: THREE.Texture) => {
    if (!t) return undefined;
    const c = t.clone();
    c.repeat.set(repeat[0], repeat[1]);
    c.needsUpdate = true;
    return c;
  };
  return new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: 0.55,
    metalness: 0.05,
    envMapIntensity: 1.15,
    ...opts,
    map: cloneMap(set?.map) ?? opts.map,
    normalMap: cloneMap(set?.normalMap) ?? opts.normalMap,
    roughnessMap: cloneMap(set?.roughnessMap) ?? opts.roughnessMap,
    normalScale: opts.normalScale ?? new THREE.Vector2(0.7, 0.7),
  });
}

export function buildMaterials(tex: TextureLibrary, env: THREE.Texture): MatLib {
  const wet = {
    clearcoat: 0.72,
    clearcoatRoughness: 0.28,
    sheen: 0.12,
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0x8aa0b8),
    envMap: env,
    envMapIntensity: 1.35,
  };

  const mats: MatLib = {
    concrete: pbr(tex.concrete, { ...wet, roughness: 0.52, color: 0xc9cdd1, normalScale: new THREE.Vector2(0.45, 0.45) }, [6, 6]),
    asphalt: pbr(tex.asphalt, { ...wet, roughness: 0.5, metalness: 0.06, color: 0x8a8e94, normalScale: new THREE.Vector2(0.35, 0.35) }, [8, 8]),
    rust: pbr(tex.rust, { envMap: env, roughness: 0.62, metalness: 0.55, clearcoat: 0.15 }, [3, 3]),
    gray: pbr(tex.paintGray, { ...wet, metalness: 0.35, roughness: 0.4 }, [2, 2]),
    orange: pbr(tex.paintOrange, { ...wet, metalness: 0.28, roughness: 0.38, color: 0xffffff }, [1, 2]),
    teal: pbr(tex.paintTeal, { ...wet, metalness: 0.28, roughness: 0.38 }, [1, 2]),
    diamond: pbr(tex.diamond, { ...wet, metalness: 0.7, roughness: 0.32 }, [4, 4]),
    hazard: pbr(tex.hazard, { ...wet, metalness: 0.15, roughness: 0.45 }, [2, 2]),
    plaster: pbr(tex.plaster, { envMap: env, roughness: 0.78, metalness: 0.02, clearcoat: 0.08 }, [4, 4]),
    puddle: new THREE.MeshPhysicalMaterial({
      color: 0x141a20,
      metalness: 0.22,
      roughness: 0.18,
      clearcoat: 0.7,
      clearcoatRoughness: 0.2,
      envMap: env,
      envMapIntensity: 1.15,
      transparent: true,
      opacity: 0.55,
      normalMap: tex.waterN,
      normalScale: new THREE.Vector2(0.22, 0.22),
    }),
    glass: new THREE.MeshPhysicalMaterial({
      color: 0x8aa0aa,
      metalness: 0.1,
      roughness: 0.05,
      transmission: 0.55,
      thickness: 0.2,
      transparent: true,
      opacity: 0.55,
      envMap: env,
      envMapIntensity: 1.6,
    }),
    emissiveSodium: new THREE.MeshStandardMaterial({
      color: 0x22180a,
      emissive: new THREE.Color(0xffb347),
      emissiveIntensity: 2.1,
      roughness: 0.4,
      metalness: 0.1,
    }),
    emissiveLed: new THREE.MeshStandardMaterial({
      color: 0x061018,
      emissive: new THREE.Color(0x7ec8e3),
      emissiveIntensity: 1.9,
      roughness: 0.35,
    }),
    emissiveRed: new THREE.MeshStandardMaterial({
      color: 0x140404,
      emissive: new THREE.Color(0xff2a2a),
      emissiveIntensity: 1.7,
      roughness: 0.4,
    }),
    black: new THREE.MeshPhysicalMaterial({
      color: 0x121417,
      roughness: 0.45,
      metalness: 0.4,
      envMap: env,
      clearcoat: 0.3,
    }),
    tan: new THREE.MeshPhysicalMaterial({
      color: 0x8a7354,
      roughness: 0.7,
      metalness: 0.05,
      envMap: env,
    }),
    water: new THREE.MeshPhysicalMaterial({
      color: 0x0b1a22,
      metalness: 0.4,
      roughness: 0.12,
      clearcoat: 1,
      envMap: env,
      envMapIntensity: 1.8,
      normalMap: tex.waterN,
      normalScale: new THREE.Vector2(0.55, 0.55),
    }),
    gunMetal: new THREE.MeshPhysicalMaterial({
      color: 0x2a2d32,
      metalness: 0.92,
      roughness: 0.28,
      envMap: env,
      envMapIntensity: 1.4,
      clearcoat: 0.45,
      clearcoatRoughness: 0.25,
    }),
    gunDark: new THREE.MeshPhysicalMaterial({
      color: 0x141518,
      metalness: 0.4,
      roughness: 0.62,
      envMap: env,
    }),
    gunAccent: new THREE.MeshPhysicalMaterial({
      color: 0x3d3f3a,
      metalness: 0.12,
      roughness: 0.62,
      envMap: env,
    }),
    gunGlass: new THREE.MeshPhysicalMaterial({
      color: 0x0a120c,
      metalness: 0,
      roughness: 0.05,
      transparent: true,
      opacity: 0.32,
      envMap: env,
      envMapIntensity: 1.4,
    }),
  };

  for (const m of Object.values(mats)) {
    m.shadowSide = THREE.FrontSide;
  }
  return mats;
}
